import { useRef, useState, useMemo, useContext } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { GoogleLogin } from "@react-oauth/google";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
} from "react-icons/fa";
import Modal from "../components/Modal";
import OtpModal from "../components/OtpModal";
import { authSchemas } from "../validation/schemas";
import { validateForm, sanitizeFormData } from "../validation/validate";
import useAuthFormKeyboardScroll from "../hooks/useAuthFormKeyboardScroll";
import "./Login.css";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const [form, setForm] = useState({
    username: "",
    name: "",
    email: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [googleLoading, setGoogleLoading] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const authPanelRef = useAuthFormKeyboardScroll();

  const passwordStrength = useMemo(() => {
    if (!form.password) return { strength: 0, label: "", hint: "" };

    let strength = 0;
    if (form.password.length >= 6) strength++;
    if (form.password.length >= 8) strength++;
    if (/[a-z]/.test(form.password) && /[A-Z]/.test(form.password)) strength++;
    if (/\d/.test(form.password)) strength++;
    if (/[^a-zA-Z\d]/.test(form.password)) strength++;

    const labels = ["Very weak", "Weak", "Fair", "Good", "Strong"];
    const capped = Math.min(strength, 4);

    let hint = "";
    if (form.password.length < 6) {
      hint = "At least 6 characters";
    } else if (
      !/[a-z]/.test(form.password) &&
      /[A-Z]/.test(form.password)
    ) {
      hint = "Add a lowercase letter";
    } else if (
      /[a-z]/.test(form.password) &&
      !/[A-Z]/.test(form.password)
    ) {
      hint = "Add an uppercase letter";
    } else if (!/\d/.test(form.password)) {
      hint = "Add a number";
    } else if (!/[^a-zA-Z\d]/.test(form.password)) {
      hint = "Add a special character";
    } else if (capped >= 4) {
      hint = "Strong password";
    }

    return {
      strength: capped,
      label: labels[capped],
      hint,
    };
  }, [form.password]);

  const passwordsMatch = useMemo(() => {
    if (!confirmPassword) return null;
    return form.password === confirmPassword;
  }, [form.password, confirmPassword]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/google"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ token: credentialResponse.credential }),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        setUser(data.user);

        try {
          const profileRes = await fetch(
            getApiUrl("/api/user/profile"),
            {
              credentials: "include",
            }
          );
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            if (profileData.user) {
              localStorage.setItem("user", JSON.stringify(profileData.user));
              setUser(profileData.user);
            }
          }
        } catch (profileError) {
          console.error(
            "Failed to fetch profile after Google signup:",
            profileError
          );
        }

        if (data.isNewUser || !data.user.username) {
          navigate("/profile");
        } else {
          navigate("/");
        }
      } else {
        setModal({
          open: true,
          title: "Error",
          message: data.error || data.msg || "Google signup failed",
          type: "error",
        });
      }
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setModal({
      open: true,
      title: "Error",
      message: "Google signup was cancelled or failed",
      type: "error",
    });
    setGoogleLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "confirmPassword") {
      setConfirmPassword(value);
    } else {
      setForm({ ...form, [name]: value });
    }

    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "username") {
      const trimmed = value.trim();
      if (trimmed !== value) {
        setForm((prev) => ({ ...prev, username: trimmed }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = { ...form, confirmPassword };
    const validation = await validateForm(authSchemas.register, formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    const sanitizedForm = sanitizeFormData(validation.data);

    setLoading(true);
    setSendingOtp(true);
    setRegistrationSuccess(false);

    try {
      const res = await fetch(getApiUrl("/api/auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sanitizedForm),
      });

      const data = await res.json();
      setLoading(false);
      setSendingOtp(false);

      if (res.ok) {
        setRegistrationSuccess(true);
        setRegisteredEmail(form.email);
        setTimeout(() => {
          setShowOtp(true);
        }, 500);
      } else {
        const message = String(data.error || data.msg || "Registration failed");
        const isDuplicateUsername =
          /username\s*(is)?\s*already\s*taken|duplicate\s*key.*username/i.test(
            message
          );
        const isDuplicateEmail =
          /account\s*with\s*this\s*email\s*already\s*exists|duplicate\s*key.*email/i.test(
            message
          );
        if (isDuplicateUsername) {
          setModal({
            open: true,
            title: "Pen name unavailable",
            message:
              "That pen name is already taken. Please choose another one.",
            type: "error",
          });
          setTimeout(() => {
            try {
              usernameRef.current && usernameRef.current.focus();
            } catch {}
          }, 50);
        } else if (isDuplicateEmail) {
          const isGoogleAccount =
            data.code === "GOOGLE_ACCOUNT" || /google/i.test(message);
          const shouldLogIn =
            data.code === "ACCOUNT_EXISTS" || /log in/i.test(message);
          setModal({
            open: true,
            title: isGoogleAccount ? "Use Google sign-in" : "You already have an account",
            message: isGoogleAccount
              ? "This email is already linked to a Google account. Sign in with Google on the login page."
              : shouldLogIn
                ? data.username
                  ? `Pen name "${data.username}" is already registered with this email. Log in with your password, or use Forgot password if you don't remember it.`
                  : "An account with this email already exists. Please log in instead."
                : "An account with this email already exists. Use another email or log in.",
            type: "error",
            confirmText: shouldLogIn ? "Go to login" : undefined,
            onConfirm: shouldLogIn
              ? () => {
                  setModal((prev) => ({ ...prev, open: false }));
                  navigate("/login");
                }
              : undefined,
          });
          setTimeout(() => {
            try {
              emailRef.current && emailRef.current.focus();
            } catch {}
          }, 50);
        } else {
          setModal({
            open: true,
            title: "Error",
            message,
            type: "error",
          });
        }
      }
    } catch (error) {
      setLoading(false);
      setSendingOtp(false);
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    }
  };

  return (
    <div className="auth-page auth-page--register">
      {(loading || sendingOtp) && (
        <div className="auth-page__overlay">
          <div className="auth-page__overlay-card">
            <FaSpinner className="text-4xl text-[#f26522] animate-spin mx-auto mb-4" />
            <p className="font-medium text-[#333333] mb-1">Sending verification code…</p>
            <p className="text-sm text-[#777777]">Please wait</p>
          </div>
        </div>
      )}
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() =>
          setModal({ open: false, title: "", message: "", type: "info" })
        }
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText}
      />
      <OtpModal
        open={showOtp}
        email={registeredEmail}
        onClose={() => {
          setShowOtp(false);
          setRegistrationSuccess(false);
        }}
        onVerified={(data) => {
          setShowOtp(false);
          setRegistrationSuccess(false);

          if (data?.user) {
            localStorage.setItem("user", JSON.stringify(data.user));
            if (data.token) {
              localStorage.setItem("token", data.token);
            }
            setUser(data.user);
            navigate("/");
            return;
          }

          setModal({
            open: true,
            title: "Success",
            message: "Email verified! You can now log in.",
            type: "success",
          });
          setTimeout(() => navigate("/login"), 1200);
        }}
      />

      <div className="auth-page__layout">
        <aside className="auth-page__brand">
          <Link to="/">
            <img
              src="/logo.png"
              srcSet="/logo.png 1x, /logo@2x.png 2x"
              alt=""
              className="auth-page__brand-logo"
            />
          </Link>
          <h2 className="auth-page__brand-title">Join the community.</h2>
          <p className="auth-page__brand-text">
            Create your account to publish poems, join writing groups, and
            connect with poets who share your voice.
          </p>
          <blockquote className="auth-page__brand-quote">
            “Every artist was first an amateur.”
          </blockquote>
        </aside>

        <div className="auth-page__panel" ref={authPanelRef}>
          <div className="auth-page__card">
            <Link to="/">
              <img
                src="/logo.png"
                srcSet="/logo.png 1x, /logo@2x.png 2x"
                alt="PeerPoetry"
                className="auth-page__mobile-logo"
              />
            </Link>

            <h1 className="auth-page__title">Create account</h1>
            <p className="auth-page__subtitle">
              A few details and you&apos;re ready to write.
            </p>

            {registrationSuccess && (
              <div className="auth-page__banner" role="status">
                <FaCheckCircle aria-hidden="true" />
                <span>Verification code sent to your email.</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-page__form auth-page__form--register">
              <div className="auth-page__form-row">
              <div className="auth-page__field">
                <label htmlFor="username" className="auth-page__label">
                  Username / pen name
                </label>
                <div className="auth-page__input-wrap">
                  <FaUser className="auth-page__input-icon" aria-hidden="true" />
                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Your username or pen name"
                    value={form.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`auth-page__input${
                      formErrors.username ? " auth-page__input--error" : ""
                    }`}
                    required
                    ref={usernameRef}
                    autoComplete="username"
                    aria-invalid={formErrors.username ? "true" : "false"}
                  />
                </div>
                {formErrors.username && (
                  <p className="auth-page__error" role="alert">
                    {formErrors.username}
                  </p>
                )}
              </div>

              <div className="auth-page__field">
                <label htmlFor="name" className="auth-page__label">
                  Display name <span className="auth-page__optional">(optional)</span>
                </label>
                <div className="auth-page__input-wrap">
                  <FaUser className="auth-page__input-icon" aria-hidden="true" />
                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="How others see you"
                    value={form.name}
                    onChange={handleChange}
                    className={`auth-page__input${
                      formErrors.name ? " auth-page__input--error" : ""
                    }`}
                    autoComplete="name"
                  />
                </div>
                {formErrors.name && (
                  <p className="auth-page__error" role="alert">
                    {formErrors.name}
                  </p>
                )}
              </div>
              </div>

              <div className="auth-page__field">
                <label htmlFor="email" className="auth-page__label">
                  Email
                </label>
                <div className="auth-page__input-wrap">
                  <FaEnvelope className="auth-page__input-icon" aria-hidden="true" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={handleChange}
                    className={`auth-page__input${
                      formErrors.email ? " auth-page__input--error" : ""
                    }`}
                    required
                    ref={emailRef}
                    autoComplete="email"
                    aria-invalid={formErrors.email ? "true" : "false"}
                  />
                </div>
                {formErrors.email && (
                  <p className="auth-page__error" role="alert">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className="auth-page__form-row">
              <div className="auth-page__field">
                <label htmlFor="password" className="auth-page__label">
                  Password
                </label>
                <div className="auth-page__input-wrap">
                  <FaLock className="auth-page__input-icon" aria-hidden="true" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Create a password"
                    value={form.password}
                    onChange={handleChange}
                    className={`auth-page__input auth-page__input--with-toggle${
                      formErrors.password ? " auth-page__input--error" : ""
                    }`}
                    required
                    autoComplete="new-password"
                    aria-invalid={formErrors.password ? "true" : "false"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-page__input-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {form.password && (
                  <div className="auth-page__strength">
                    <div className="auth-page__strength-bar">
                      <div className="auth-page__strength-track">
                        <div
                          className={`auth-page__strength-fill auth-page__strength-fill--${passwordStrength.strength}`}
                          style={{
                            width: `${((passwordStrength.strength + 1) / 5) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="auth-page__strength-label">
                        {passwordStrength.label}
                      </span>
                    </div>
                    {passwordStrength.hint && (
                      <p className="auth-page__strength-hint">
                        {passwordStrength.hint}
                      </p>
                    )}
                  </div>
                )}
                {formErrors.password && (
                  <p className="auth-page__error" role="alert">
                    {formErrors.password}
                  </p>
                )}
              </div>

              <div className="auth-page__field">
                <label htmlFor="confirmPassword" className="auth-page__label">
                  Confirm password
                </label>
                <div className="auth-page__input-wrap">
                  <FaLock className="auth-page__input-icon" aria-hidden="true" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={handleChange}
                    className={`auth-page__input auth-page__input--with-toggle${
                      formErrors.confirmPassword ||
                      passwordsMatch === false
                        ? " auth-page__input--error"
                        : ""
                    }`}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="auth-page__input-toggle"
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {confirmPassword && passwordsMatch !== null && (
                  <div
                    className={`auth-page__match ${
                      passwordsMatch
                        ? "auth-page__match--ok"
                        : "auth-page__match--bad"
                    }`}
                  >
                    {passwordsMatch ? (
                      <>
                        <FaCheckCircle aria-hidden="true" />
                        <span>Passwords match</span>
                      </>
                    ) : (
                      <>
                        <FaExclamationCircle aria-hidden="true" />
                        <span>Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
                {formErrors.confirmPassword && (
                  <p className="auth-page__error" role="alert">
                    {formErrors.confirmPassword}
                  </p>
                )}
              </div>
              </div>

              <button
                type="submit"
                disabled={loading || sendingOtp}
                className="auth-page__submit flex items-center justify-center gap-2"
              >
                {loading || sendingOtp ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Sending verification code…
                  </>
                ) : (
                  "Create account"
                )}
              </button>
            </form>

            <div className="auth-page__divider">Or</div>
            <div className="auth-page__google">
              {googleLoading ? (
                <button
                  type="button"
                  disabled
                  className="auth-page__submit opacity-50 cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FaSpinner className="animate-spin" />
                  Signing up with Google…
                </button>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="signup_with"
                  shape="rectangular"
                  logo_alignment="left"
                />
              )}
            </div>

            <div className="auth-page__footer">
              <p className="auth-page__footer-text">Already have an account?</p>
              <button
                type="button"
                className="auth-page__footer-link"
                onClick={() => navigate("/login")}
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
